import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button, Combobox, EmptyState, ViewToggle } from "../../../../components/ui";
import { useDebounce } from "../../../../hooks/useDebounce";
import { qk } from "../../../../lib/queryKeys";
import { useDarkMode } from "../../../../context/ThemeContext";
import { useTrackingLinksList } from "../../../../features/trackingLinks/queries";
import { allUnitsQueryOptions } from "../../../../features/units/queries";
import {
  useTrackingLinksStore,
  type TrackingLink,
} from "../../../../store/admin/trackingLinksStore";
import { useCompaniesStore } from "../../../../store/admin/companiesStore";
import { panelThemeClasses } from "../../../../theme/themeClasses";
import { TrackingLinkDetailsModal } from "../../../admin/components/sections/trackingLinks/components/TrackingLinkDetailsModal";
import {
  TrackingLinkModal,
  type TrackingLinkFormData,
} from "../../../admin/components/sections/trackingLinks/components";
import { resolveStatus } from "../../../admin/components/sections/trackingLinks/utils/helpers";
import TrackingLinkCard from "./components/TrackingLinkCard";

const ACTIVE_STATUS_ID = "R5tW0uX4yY9cF6dH2aI8";
const INACTIVE_STATUS_ID = "S6uX1vY5zZ0dG7eI3bJ9";
const PAGE_SIZE = 24;

const EMPTY_FORM: TrackingLinkFormData = {
  name: "",
  internalNotes: "",
  units: [],
  shareTrips: false,
  unitSelection: "none",
  accessType: "public",
  accessCode: "",
  activationType: "immediate",
  activationDate: "",
  durationValue: 24,
  durationUnit: "hours",
};

type StatusFilter = "all" | "active" | "inactive" | "expired";
type AccessFilter = "all" | "public" | "protected";

export default function TrackingLinksView() {
  const { t } = useTranslation("envios");
  const { isDarkMode } = useDarkMode();
  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const createTrackingLink = useTrackingLinksStore((s) => s.createTrackingLink);
  const updateTrackingLink = useTrackingLinksStore((s) => s.updateTrackingLink);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [accessFilter, setAccessFilter] = useState<AccessFilter>("all");
  const [search, setSearch] = useState("");
  const [detailsLink, setDetailsLink] = useState<TrackingLink | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editLink, setEditLink] = useState<TrackingLink | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const debouncedSearch = useDebounce(search);
  const themeClasses = panelThemeClasses();

  const form = useForm<TrackingLinkFormData>({
    mode: "onChange",
    defaultValues: EMPTY_FORM,
  });

  const unitsQuery = useQuery({
    ...allUnitsQueryOptions(companyId),
    enabled: !!companyId && formOpen,
  });
  const units = unitsQuery.data ?? [];

  useEffect(() => () => {
    document.body.style.overflow = "";
  }, []);

  const params = useMemo(
    () => ({
      page: 1,
      limit: PAGE_SIZE,
      accessType: accessFilter !== "all" ? accessFilter : undefined,
      status: statusFilter !== "all" ? statusFilter : undefined,
      search: debouncedSearch || undefined,
    }),
    [accessFilter, statusFilter, debouncedSearch],
  );

  const { data, isLoading, isError } = useTrackingLinksList(params);
  const links = data?.data ?? [];

  const counts = useMemo(() => {
    const byStatus = links.map((link) => resolveStatus(link.status));
    return {
      active: byStatus.filter((s) => s === "active").length,
      inactive: byStatus.filter((s) => s === "inactive" || s === "pending").length,
      expired: byStatus.filter((s) => s === "expired").length,
    };
  }, [links]);

  const handleCopy = (link: TrackingLink) => {
    navigator.clipboard
      .writeText(`${window.location.origin}${link.publicUrl}`)
      .then(() => toast.success(t("trackingLinks.messages.copied", { name: link.name })))
      .catch(() => toast.error(t("detail.linkError")));
  };

  const invalidateLinks = () => {
    queryClient.invalidateQueries({ queryKey: ["trackingLinks"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  const openForm = () => {
    setFormOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleAddNew = () => {
    form.reset(EMPTY_FORM);
    setEditLink(null);
    openForm();
  };

  const handleEdit = (link: TrackingLink) => {
    form.reset({
      name: link.name,
      internalNotes: link.internalNotes || "",
      units: link.units || [],
      shareTrips: !!link.shareTrips,
      unitSelection: link.units?.length > 0 ? "specific" : "none",
      accessType: link.accessType,
      accessCode: link.hasAccessCode ? "••••••••" : "",
      activationType: link.activationType,
      activationDate: link.activationDate || "",
      durationValue: link.durationValue,
      durationUnit: link.durationUnit,
    });
    setEditLink(link);
    openForm();
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setEditLink(null);
    setLoadingSubmit(false);
    form.reset(EMPTY_FORM);
    document.body.style.overflow = "";
  };

  const resolveUnits = (data: TrackingLinkFormData) => {
    if (data.unitSelection === "all") return units.map((unit) => unit.id);
    return data.unitSelection === "none" ? [] : data.units;
  };

  const settle = (result: TrackingLink | null, name: string, isEdit: boolean) => {
    setLoadingSubmit(false);

    if (!result) {
      toast.error(
        isEdit
          ? t("trackingLinks.messages.updateError")
          : t("trackingLinks.messages.createError"),
      );
      return;
    }

    invalidateLinks();
    toast.success(
      isEdit
        ? t("trackingLinks.messages.updated", { name })
        : t("trackingLinks.messages.created", { name }),
    );
    handleCloseForm();
  };

  const handleSubmit = async (data: TrackingLinkFormData) => {
    const shared = {
      name: data.name,
      internalNotes: data.internalNotes,
      units: resolveUnits(data),
      shareTrips: !!data.shareTrips,
      accessType: data.accessType,
      accessCode: data.accessType === "protected" ? data.accessCode : undefined,
      durationValue: data.durationValue,
      durationUnit: data.durationUnit,
    };

    if (editLink) {
      setLoadingSubmit(true);
      settle(await updateTrackingLink(editLink.id, shared), data.name, true);
      return;
    }

    if (!companyId) {
      toast.error(t("trackingLinks.messages.noCompanySelected"));
      return;
    }

    setLoadingSubmit(true);
    const created = await createTrackingLink({
      ...shared,
      activationType: data.activationType,
      activationDate:
        data.activationType === "scheduled" ? data.activationDate : undefined,
      company_id: companyId,
    });
    settle(created, data.name, false);
  };

  const handleToggleActive = async (link: TrackingLink, activate: boolean) => {
    const wasExpired = resolveStatus(link.status) === "expired";

    setTogglingId(link.id);
    const result = await updateTrackingLink(link.id, {
      status_id: activate ? ACTIVE_STATUS_ID : INACTIVE_STATUS_ID,
    });
    setTogglingId(null);

    if (!result) {
      toast.error(t("trackingLinks.messages.updateError"));
      return;
    }

    invalidateLinks();

    if (!activate) {
      toast.success(t("trackingLinks.messages.deactivated", { name: link.name }));
      return;
    }

    toast.success(
      wasExpired
        ? t("trackingLinks.messages.renewed", { name: link.name })
        : t("trackingLinks.messages.activated", { name: link.name }),
    );
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setAccessFilter("all");
    setSearch("");
  };

  const accessOptions: { value: AccessFilter; label: string }[] = [
    { value: "all", label: t("trackingLinks.filters.accessType") },
    { value: "public", label: t("trackingLinks.access.public") },
    { value: "protected", label: t("trackingLinks.access.protected") },
  ];

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: "all", label: t("trackingLinks.filters.status") },
    { value: "active", label: t("trackingLinks.status.active") },
    { value: "inactive", label: t("trackingLinks.status.inactive") },
    { value: "expired", label: t("trackingLinks.status.expired") },
  ];

  return (
    <div className="space-y-5 px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold text-content">
            {t("trackingLinks.title")}
          </h1>
          <p className="text-sm text-content-secondary mt-1">
            {t("trackingLinks.subtitle")}
          </p>
        </div>
        <Button
          size="sm"
          icon={<i className="ri-add-line" />}
          onClick={handleAddNew}
          className="shrink-0"
        >
          {t("trackingLinks.newLink")}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-primary-500/10 border border-primary-500/30 rounded-xl px-4 py-3">
          <div className="text-xs text-primary-500 font-medium mb-0.5">
            {t("trackingLinks.status.active")}
          </div>
          <div className="text-2xl font-bold text-primary-500 font-mono">
            {counts.active}
          </div>
        </div>
        <div className="bg-subtle border border-line rounded-xl px-4 py-3">
          <div className="text-xs text-content-muted font-medium mb-0.5">
            {t("trackingLinks.status.inactive")}
          </div>
          <div className="text-2xl font-bold text-content font-mono">
            {counts.inactive}
          </div>
        </div>
        <div className="bg-error/10 border border-error/30 rounded-xl px-4 py-3">
          <div className="text-xs text-error font-medium mb-0.5">
            {t("trackingLinks.status.expired")}
          </div>
          <div className="text-2xl font-bold text-error font-mono">
            {counts.expired}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
        <div className="w-full sm:w-[190px]">
          <Combobox
            value={accessOptions.find((option) => option.value === accessFilter) ?? null}
            onChange={(option) => setAccessFilter(option?.value ?? "all")}
            options={accessOptions}
            isSearchable={false}
            aria-label={t("trackingLinks.filters.accessType")}
          />
        </div>

        <div className="w-full sm:w-[170px]">
          <Combobox
            value={statusOptions.find((option) => option.value === statusFilter) ?? null}
            onChange={(option) => setStatusFilter(option?.value ?? "all")}
            options={statusOptions}
            isSearchable={false}
            aria-label={t("trackingLinks.filters.status")}
          />
        </div>

        <div className="relative flex-1">
          <span className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center">
            <i className="ri-search-line text-content-muted text-sm" />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("trackingLinks.filters.searchPlaceholder")}
            className="w-full h-10 pl-9 pr-3 rounded-xl text-sm border border-line bg-app text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <ViewToggle
          value={viewMode}
          onChange={setViewMode}
          labels={{ grid: t("buttons.list"), list: t("buttons.kanban") }}
        />
      </div>

      {isLoading ? (
        <div className="flex min-h-64 items-center justify-center py-12">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        </div>
      ) : isError ? (
        <EmptyState
          icon="ri-error-warning-line"
          title={t("trackingLinks.messages.loadError")}
        />
      ) : links.length === 0 ? (
        <EmptyState
          icon="ri-link"
          title={t("trackingLinks.empty.title")}
          description={t("trackingLinks.empty.description")}
          action={
            <Button variant="link" size="sm" onClick={clearFilters}>
              {t("trackingLinks.empty.clearFilters")}
            </Button>
          }
        />
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {links.map((link) => (
            <TrackingLinkCard
              key={link.id}
              link={link}
              viewMode="grid"
              isToggling={togglingId === link.id}
              onCopy={handleCopy}
              onDetails={setDetailsLink}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {links.map((link) => (
            <TrackingLinkCard
              key={link.id}
              link={link}
              viewMode="list"
              isToggling={togglingId === link.id}
              onCopy={handleCopy}
              onDetails={setDetailsLink}
              onEdit={handleEdit}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}

      <TrackingLinkModal
        isOpen={formOpen}
        isEdit={!!editLink}
        loadingSubmit={loadingSubmit}
        form={form}
        themeClasses={themeClasses}
        isDarkMode={isDarkMode}
        units={units}
        onSubmit={handleSubmit}
        onClose={handleCloseForm}
      />

      <TrackingLinkDetailsModal
        isOpen={!!detailsLink}
        onClose={() => setDetailsLink(null)}
        trackingLink={detailsLink}
        themeClasses={themeClasses}
      />
    </div>
  );
}
